/**
 * Joycelyn Fashion — 360° Studio Spin & 3D Garment Viewer
 * Robust selector matching, click buttons, touch/mouse drag, and <model-viewer> activation.
 */
class GarmentViewer {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.tabButtons = wrapper.querySelectorAll('.viewer-capsule-btn, .viewer-tab-btn, [data-viewer-mode]');
    this.modelViewer = wrapper.querySelector('model-viewer');
    this.spinContainer = wrapper.querySelector('.spin-360-container');
    this.frames = wrapper.querySelectorAll('.spin-360-frame');
    this.angleDisplay = wrapper.querySelector('[data-angle-display]');
    this.prevBtn = wrapper.querySelector('[data-spin-prev]');
    this.nextBtn = wrapper.querySelector('[data-spin-next]');
    this.autoRotateBtn = wrapper.querySelector('[data-auto-spin]');
    
    this.currentFrame = 0;
    this.isDragging = false;
    this.startX = 0;
    this.autoSpinTimer = null;

    this.initTabs();
    this.init360Spin();
    this.initControls();
  }

  initTabs() {
    this.tabButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const mode = btn.getAttribute('data-viewer-mode');
        this.tabButtons.forEach(b => {
          b.classList.remove('is-active');
          b.style.background = 'transparent';
          b.style.color = 'var(--color-text-muted)';
        });
        btn.classList.add('is-active');
        btn.style.background = 'var(--color-purple)';
        btn.style.color = '#FFFFFF';

        if (mode === '3d' && this.modelViewer) {
          this.modelViewer.style.display = 'block';
          if (this.spinContainer) this.spinContainer.style.display = 'none';
          if (this.wrapper.querySelector('.spin-controls-bar')) {
            this.wrapper.querySelector('.spin-controls-bar').style.display = 'none';
          }
        } else if (mode === '360' && this.spinContainer) {
          this.spinContainer.style.display = 'block';
          if (this.modelViewer) this.modelViewer.style.display = 'none';
          if (this.wrapper.querySelector('.spin-controls-bar')) {
            this.wrapper.querySelector('.spin-controls-bar').style.display = 'flex';
          }
        }
      });
    });
  }

  init360Spin() {
    if (!this.spinContainer || this.frames.length === 0) return;

    const onStart = (clientX) => {
      this.isDragging = true;
      this.startX = clientX;
      this.stopAutoSpin();
    };

    const onMove = (clientX) => {
      if (!this.isDragging) return;
      const deltaX = clientX - this.startX;
      if (Math.abs(deltaX) > 15) {
        const direction = deltaX > 0 ? -1 : 1;
        this.stepFrame(direction);
        this.startX = clientX;
      }
    };

    const onEnd = () => {
      this.isDragging = false;
    };

    this.spinContainer.addEventListener('mousedown', (e) => onStart(e.clientX));
    window.addEventListener('mousemove', (e) => onMove(e.clientX));
    window.addEventListener('mouseup', onEnd);

    this.spinContainer.addEventListener('touchstart', (e) => onStart(e.touches[0].clientX), { passive: true });
    this.spinContainer.addEventListener('touchmove', (e) => onMove(e.touches[0].clientX), { passive: true });
    this.spinContainer.addEventListener('touchend', onEnd);
  }

  initControls() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.stopAutoSpin();
        this.stepFrame(-1);
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.stopAutoSpin();
        this.stepFrame(1);
      });
    }

    if (this.autoRotateBtn) {
      this.autoRotateBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (this.autoSpinTimer) {
          this.stopAutoSpin();
        } else {
          this.startAutoSpin();
        }
      });
    }
  }

  stepFrame(dir) {
    if (this.frames.length === 0) return;
    this.frames[this.currentFrame].classList.remove('is-visible');
    this.frames[this.currentFrame].style.display = 'none';
    this.currentFrame = (this.currentFrame + dir + this.frames.length) % this.frames.length;
    this.frames[this.currentFrame].classList.add('is-visible');
    this.frames[this.currentFrame].style.display = 'block';

    if (this.angleDisplay) {
      const angleStep = 360 / this.frames.length;
      const degrees = Math.round(this.currentFrame * angleStep);
      this.angleDisplay.textContent = degrees + '° View';
    }
  }

  startAutoSpin() {
    if (this.autoSpinTimer) return;
    this.autoRotateBtn.textContent = '❚❚ Pause Spin';
    this.autoSpinTimer = setInterval(() => this.stepFrame(1), 1200);
  }

  stopAutoSpin() {
    if (this.autoSpinTimer) {
      clearInterval(this.autoSpinTimer);
      this.autoSpinTimer = null;
      if (this.autoRotateBtn) this.autoRotateBtn.textContent = '▶ Auto Spin';
    }
  }
}

function initGarmentViewer() {
  document.querySelectorAll('.garment-viewer-wrapper').forEach(w => new GarmentViewer(w));
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initGarmentViewer);
} else {
  initGarmentViewer();
}
